/// <amd-module name="Wit/DeliveryPlans/Common/WorkItem" />

import { ParentChildWIMap } from "Wit/Clients/Work/Contracts/Work";
import { ClientWorkItem, ObservableClientWorkItem, ParentField } from "Wit/ClientWorkItemService/IClientWorkItem";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { FieldRefNames } from "./Constants";
import { IDeliveryPlansWorkItem, IIteration, IObservableDeliveryPlansWorkItem } from "./Interfaces";
import { IDeliveryPlansIterationService } from "./Services/DeliveryPlansIterationService";

export class DeliveryPlansWorkItem extends ClientWorkItem implements IDeliveryPlansWorkItem {
    private _iterationService: IDeliveryPlansIterationService;

    public partial: boolean;

    public get startDate(): Date {
        const startDate = this.getFieldValue<Date>(FieldRefNames.StartDate);
        const endDate = this.getFieldValue<Date>(FieldRefNames.TargetDate);
        const useIteration = !startDate || (!endDate && startDate > this.iteration.endDate);
        return useIteration ? this.iteration.startDate : startDate!;
    }

    public get endDate(): Date {
        const startDate = this.getFieldValue<Date>(FieldRefNames.StartDate);
        const endDate = this.getFieldValue<Date>(FieldRefNames.TargetDate);
        const useIteration = !endDate || (!startDate && endDate < this.iteration.startDate);
        return useIteration ? this.iteration.endDate : endDate!;
    }

    public get iteration(): IIteration {
        const iterationPath = this.getFieldValue<string>(CoreFieldRefNames.IterationPath);
        const iteration = iterationPath && this._iterationService.getIteration(iterationPath);
        if (!iteration) {
            throw Error(`Could not find iteration ${iterationPath}`);
        }
        return iteration;
    }

    public constructor(
        fields: INamedFieldDataDictionary,
        iterationService: IDeliveryPlansIterationService,
        referenceFields?: IFieldEntry[],
        partial = false
    ) {
        super(fields, referenceFields);
        this._iterationService = iterationService;
        this.partial = partial;
    }

    public getIteration(): IIteration | undefined {
        const iterationPath = this.getFieldValue<string>(CoreFieldRefNames.IterationPath);
        return iterationPath ? this._iterationService.getIteration(iterationPath) : undefined;
    }

    public setFieldValues(fields: { [fieldName: string]: unknown }): void {
        // TODO: this doesn't work when the item is hydrated, but does it matter?
        Object.assign(this._data, fields);
    }

    public getFields(): INamedFieldDataDictionary {
        return this._data;
    }
}

export class ObservableDeliveryPlansWorkItem extends ObservableClientWorkItem<IDeliveryPlansWorkItem> implements IObservableDeliveryPlansWorkItem {}

export function getWorkItemFromValues(
    values: any[],
    fieldReferenceNames: string[],
    iterationService: IDeliveryPlansIterationService,
    field: IFieldEntry[] | undefined,
    parentMaps: ParentChildWIMap[],
    partial = false
) {
    const fields: INamedFieldDataDictionary = {};
    for (let i = 0; i < fieldReferenceNames.length; i++) {
        let title = "";
        let workItemType = "";
        if (fieldReferenceNames[i] === CoreFieldRefNames.Parent) {
            if (values[i] !== null) {
                const parentId = fields[CoreFieldRefNames.Id] as number;
                const parentWorkItem = parentMaps.find(x => x.childWorkItemIds.indexOf(parentId) !== -1);
                if (parentWorkItem) {
                    title = parentWorkItem.title;
                    workItemType = parentWorkItem.workItemTypeName;
                }
            }
            fields[ParentField.Title] = title;
            fields[ParentField.WorkItemType] = workItemType;
        }
        fields[fieldReferenceNames[i]] = values[i];
    }

    return new DeliveryPlansWorkItem(fields, iterationService, field, partial);
}
